import { Fragment, useContext } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import {
  HomeIcon,
  FolderIcon,
  CodeBracketIcon,
  CreditCardIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ArrowRightOnRectangleIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { FolderSearch } from 'lucide-react';
import { getAuth } from 'firebase/auth';

import app from '../../utils/firebaseApp.js';
import { AuthContext } from '../../utils/auth.js';
import { isAdminEmail } from '../../utils/adminUsers.js';
import { useDevSidebar } from '../../context/DevSidebarContext.js';
import cn from '../../ui/cn.js';
import Kbd, { modKey } from '../../ui/Kbd.js';
import Avatar from '../../ui/Avatar.js';
import Dropdown from '../../ui/Dropdown.js';
import Tooltip from '../../ui/Tooltip.js';

export const navigation = [
  { key: 'dashboard', name: 'Dashboard', to: '/dashboard', icon: HomeIcon, group: 'Workspace' },
  { key: 'extractions', name: 'Data Extraction', to: '/extractions', icon: FolderIcon, group: 'Workspace' },
  { key: 'classifications', name: 'Document Classification', to: '/classifications', icon: FolderSearch, group: 'Workspace' },
  { key: 'api', name: 'API', to: '/api', icon: CodeBracketIcon, group: 'Developers' },
  { key: 'billing', name: 'Billing', to: '/billing', icon: CreditCardIcon, group: 'Account' },
  { key: 'account', name: 'Account', to: '/account', icon: UserCircleIcon, group: 'Account' },
];

const groupOrder = ['Workspace', 'Developers', 'Account'];

export function navForPath(pathname = '') {
  return navigation.find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
}

export function signOut() {
  return getAuth(app)
    .signOut()
    .catch(() => {});
}

function NavItem({ item, active, collapsed, onNavigate }) {
  const Icon = item.icon;
  const link = (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group relative flex items-center rounded-lg text-sm font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
        collapsed ? 'h-10 w-10 justify-center' : 'h-9 gap-x-3 px-2.5',
        active ? 'bg-white/15 text-white shadow-inner-sm' : 'text-indigo-100 hover:bg-white/10 hover:text-white',
      )}
    >
      {active && !collapsed && <span className="absolute inset-y-1.5 -left-2 w-0.5 rounded-full bg-white" aria-hidden="true" />}
      <Icon className={cn('h-5 w-5 shrink-0', active ? 'text-white' : 'text-indigo-200 group-hover:text-white')} aria-hidden="true" />
      {!collapsed && <span className="truncate">{item.name}</span>}
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip content={item.name} side="right">
        {link}
      </Tooltip>
    );
  }
  return link;
}

function SidebarContent({ activeKey, collapsed, onNavigate, onOpenPalette, onToggleCollapse, showCollapse }) {
  const { currentUser } = useContext(AuthContext);
  const canAccessAdmin = isAdminEmail(currentUser?.email);

  const grouped = groupOrder.map((group) => ({ group, items: navigation.filter((n) => n.group === group) }));

  return (
    <div className={cn('ex-sidebar app-sidebar flex h-full flex-col text-white', collapsed ? 'px-3' : 'px-4')}>
      {/* Brand */}
      <div className={cn('flex h-16 shrink-0 items-center', collapsed ? 'justify-center' : 'px-1')}>
        <Link to="/dashboard" onClick={onNavigate} className="flex items-center rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60">
          {collapsed ? (
            <span className="app-sidebar-collapsed-logo flex h-9 w-9 items-center justify-center rounded-xl bg-white shadow-sm">
              <img className="h-6 w-6 object-contain" src={process.env.PUBLIC_URL + '/logo192.png'} alt="Extracta LABS" />
            </span>
          ) : (
            <img className="h-7 w-auto" src={process.env.PUBLIC_URL + '/logo_extracta_labs_white.png'} alt="Extracta LABS" />
          )}
        </Link>
      </div>

      {/* Search */}
      <div className={cn('shrink-0 pb-2', collapsed && 'flex justify-center')}>
        {collapsed ? (
          <Tooltip content={`Search (${modKey} K)`} side="right">
            <button
              type="button"
              onClick={onOpenPalette}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-indigo-100 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              aria-label="Search"
            >
              <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </Tooltip>
        ) : (
          <button
            type="button"
            onClick={onOpenPalette}
            className="flex h-9 w-full items-center gap-2 rounded-lg bg-white/10 px-2.5 text-sm text-indigo-100 ring-1 ring-inset ring-white/10 transition hover:bg-white/15 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            <MagnifyingGlassIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="flex-1 text-left">Search…</span>
            <span className="flex items-center gap-0.5 text-indigo-200">
              <Kbd className="border-white/15 bg-white/10 text-indigo-100">{modKey}</Kbd>
              <Kbd className="border-white/15 bg-white/10 text-indigo-100">K</Kbd>
            </span>
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex flex-1 flex-col overflow-y-auto no-scrollbar py-2" aria-label="Main">
        <ul className="flex flex-col gap-y-5">
          {grouped.map(({ group, items }) => (
            <li key={group}>
              {!collapsed && <p className="mb-1.5 px-2.5 text-2xs font-semibold uppercase tracking-wider text-indigo-200/80">{group}</p>}
              <ul className={cn('flex flex-col gap-y-0.5', collapsed && 'items-center')}>
                {items.map((item) => (
                  <li key={item.key} className={collapsed ? '' : 'w-full'}>
                    <NavItem item={item} active={item.key === activeKey} collapsed={collapsed} onNavigate={onNavigate} />
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      <div className={cn('shrink-0 space-y-1 border-t border-white/10 py-3', collapsed && 'flex flex-col items-center')}>
        <FooterLink collapsed={collapsed} to="https://docs.extracta.ai" external icon={BookOpenIcon} label="Documentation" />
        {canAccessAdmin && <FooterLink collapsed={collapsed} to="/admin" icon={ShieldCheckIcon} label="Admin console" onNavigate={onNavigate} highlight />}
        {showCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn(
              'flex items-center rounded-lg text-sm font-medium text-indigo-100 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
              collapsed ? 'h-10 w-10 justify-center' : 'h-9 w-full gap-x-3 px-2.5',
            )}
          >
            {collapsed ? <ChevronDoubleRightIcon className="h-5 w-5" aria-hidden="true" /> : <ChevronDoubleLeftIcon className="h-5 w-5 shrink-0 text-indigo-200" aria-hidden="true" />}
            {!collapsed && (
              <span className="flex flex-1 items-center justify-between">
                Collapse
                <span className="flex items-center gap-0.5">
                  <Kbd className="border-white/15 bg-white/10 text-indigo-100">{modKey}</Kbd>
                  <Kbd className="border-white/15 bg-white/10 text-indigo-100">B</Kbd>
                </span>
              </span>
            )}
          </button>
        )}
        <UserCard collapsed={collapsed} canAccessAdmin={canAccessAdmin} />
      </div>
    </div>
  );
}

function FooterLink({ collapsed, to, icon: Icon, label, external = false, onNavigate, highlight = false }) {
  const classes = cn(
    'flex items-center rounded-lg text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
    collapsed ? 'h-10 w-10 justify-center' : 'h-9 w-full gap-x-3 px-2.5',
    highlight ? 'bg-white/10 text-white ring-1 ring-inset ring-white/15 hover:bg-white/15' : 'text-indigo-100 hover:bg-white/10 hover:text-white',
  );
  const inner = (
    <>
      <Icon className="h-5 w-5 shrink-0 text-indigo-200" aria-hidden="true" />
      {!collapsed && <span className="truncate">{label}</span>}
    </>
  );
  const el = external ? (
    <a href={to} target="_blank" rel="noopener noreferrer" className={classes} aria-label={label}>
      {inner}
    </a>
  ) : (
    <Link to={to} onClick={onNavigate} className={classes} aria-label={label}>
      {inner}
    </Link>
  );
  return collapsed ? (
    <Tooltip content={label} side="right">
      {el}
    </Tooltip>
  ) : (
    el
  );
}

function UserCard({ collapsed, canAccessAdmin }) {
  const { currentUser } = useContext(AuthContext);
  const email = currentUser?.email || '';
  const name = currentUser?.displayName || '';

  const items = [
    [
      { label: 'Your profile', to: '/account', icon: UserCircleIcon },
      { label: 'Billing & pages', to: '/billing', icon: CreditCardIcon },
      canAccessAdmin ? { label: 'Admin console', to: '/admin', icon: ShieldCheckIcon } : null,
    ].filter(Boolean),
    [{ label: 'Log out', onClick: signOut, icon: ArrowRightOnRectangleIcon, danger: true }],
  ];

  return (
    <div className={cn('pt-1', collapsed ? 'flex justify-center' : '')}>
      <Dropdown
        align="left"
        width="w-60"
        className={collapsed ? '' : 'block w-full'}
        header={
          <div className="min-w-0">
            <p className="text-2xs uppercase tracking-wider text-gray-400">Signed in as</p>
            <p className="truncate text-sm font-medium text-gray-900">{email}</p>
          </div>
        }
        items={items}
        button={
          <span
            className={cn(
              'flex items-center rounded-lg text-left transition hover:bg-white/10 focus:outline-none',
              collapsed ? 'h-10 w-10 justify-center' : 'h-11 w-full gap-x-3 px-2',
            )}
          >
            <Avatar name={name} email={email} size={collapsed ? 'md' : 'md'} inverted />
            {!collapsed && (
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-white">{name || email.split('@')[0]}</span>
                <span className="block truncate text-xs text-indigo-200">{email}</span>
              </span>
            )}
          </span>
        }
        buttonClassName={cn('block rounded-lg focus-visible:ring-2 focus-visible:ring-white/60', collapsed ? '' : 'w-full')}
        label="Account menu"
      />
    </div>
  );
}

export default function Sidebar({ mobileOpen, setMobileOpen, activeKey, onOpenPalette }) {
  const { collapsed, toggleCollapsed } = useDevSidebar();

  return (
    <>
      {/* Mobile sheet */}
      <Transition.Root show={mobileOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[60] lg:hidden" onClose={setMobileOpen}>
          <Transition.Child as={Fragment} enter="transition-opacity ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-[2px]" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child as={Fragment} enter="transition ease-snappy duration-200 transform" enterFrom="-translate-x-full" enterTo="translate-x-0" leave="transition ease-in duration-150 transform" leaveFrom="translate-x-0" leaveTo="-translate-x-full">
              <Dialog.Panel className="relative mr-14 flex w-full max-w-[18rem] flex-1">
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                  <div className="absolute left-full top-0 flex w-14 justify-center pt-4">
                    <button type="button" className="-m-2.5 rounded-lg p-2.5 text-white hover:bg-white/10" onClick={() => setMobileOpen(false)}>
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                <SidebarContent activeKey={activeKey} collapsed={false} onNavigate={() => setMobileOpen(false)} onOpenPalette={() => { setMobileOpen(false); onOpenPalette(); }} showCollapse={false} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop */}
      <aside
        className={cn(
          'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-[width] duration-200 ease-snappy',
          collapsed ? 'lg:w-[4.25rem]' : 'lg:w-64',
        )}
      >
        <SidebarContent activeKey={activeKey} collapsed={collapsed} onOpenPalette={onOpenPalette} onToggleCollapse={toggleCollapsed} showCollapse />
      </aside>
    </>
  );
}
